import React, { useState } from "react"
import { PencilIcon, XMarkIcon } from "@heroicons/react/24/outline"

import Modal from "../../../components/modal"
import Input from "../../../components/input"
import { useNana } from "../../../hooks/useNana"
import * as Commands from "../../../../model/commands"
import { EditableProjectFields, Idea, Project } from "../../../../model/types"
import IdeaBox from "../../../components/idea_box"
import { AutocompleteResult } from "../../../components/autocomplete"
import GoalAutocomplete from "../../../components/goal_autocomplete"

export default function EditProjectButton({ project }: { project: Project }) {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [selectedGoal, setSelectedGoal] = useState<Idea | null>(null)

  const { model, commander } = useNana()

  const open = () => {
    setIsOpen(true)
    setTitle(project.name)
    const goal: Idea | null = project.goalId
      ? model.getIdea(project.goalId)
      : null
    setSelectedGoal(goal)
  }

  const submit = () => {
    const fields: EditableProjectFields = {}
    if (title !== project.name) fields.name = title
    if (selectedGoal?.id !== project.goalId) fields.goalId = selectedGoal?.id
    commander.exec(
      new Commands.SetProjectFieldsCommand({ id: project.id, fields })
    )
    setIsOpen(false)
  }

  return (
    <div data-testid="edit-project-button-and-form">
      <button
        type="button"
        onClick={() => open()}
        className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
        data-testid="edit-project-button"
      >
        <PencilIcon className="h-4 w-4 text-sm" />
      </button>
      <Modal
        open={isOpen}
        primaryButton={{
          text: "Add",
          action: () => submit(),
          disabled: false,
        }}
        secondaryButton={{
          text: "Cancel",
          action: () => setIsOpen(false),
          disabled: false,
        }}
        leftButton={{
          text: "",
          action: null,
          disabled: true,
        }}
        close={() => setIsOpen(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <Input
            label="Title"
            name="title"
            type="text"
            placeholder="Title of project"
            error={null}
            disabled={false}
            value={title}
            onChange={(n) => setTitle(n)}
            testid="edit-project-title-input"
          />
          <label className="block mt-4 mb-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-50">
            Associated goal (optional)
          </label>
          {selectedGoal ? (
            <div className="flex justify-between items-center text-sm text-gray-700 dark:text-gray-100">
              <div className="flex justify-start items-center gap-3">
                <IdeaBox idea={selectedGoal} />
                <div>{selectedGoal.title}</div>
              </div>
              <div
                onClick={() => setSelectedGoal(null)}
                className="cursor-pointer"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 hover:text-gray-300" />
              </div>
            </div>
          ) : (
            <GoalAutocomplete
              select={(result: AutocompleteResult<Idea>, _filter) => {
                if (result.type == "none") return true
                const goal = result.item
                setSelectedGoal(goal)
                return true
              }}
              onEscape={() => setIsOpen(false)}
              inputStyle="form"
            />
          )}
        </form>
      </Modal>
    </div>
  )
}
